import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { ShieldCheck, Download, FileText, Lock } from "lucide-react";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { SignalsTabs } from "@/components/signals/SignalsTabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SIGNALS } from "@/constants/testIds";
import { SEVERITY } from "@/lib/alerts";
import { SOURCE_LABEL, fmtNum } from "@/lib/signals";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

function abs(ts) {
  try { return format(new Date(ts), "MMM d, yyyy · HH:mm"); } catch { return "—"; }
}

export default function SignalsRegistry() {
  const { active } = useWorkspace();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    try {
      const { data } = await api.get("/registry", { params: { workspace_id: active.id } });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    if (!active) return;
    setExporting(true);
    try {
      const res = await api.get("/registry/export.csv", { params: { workspace_id: active.id }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sema-signal-registry.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid={SIGNALS.registryRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-7xl px-6 py-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Signal registry</h1>
            <p className="mt-2 flex max-w-xl items-center gap-1.5 text-sm text-muted-foreground">
              <Lock className="h-3.5 w-3.5 text-accent" /> Append-only. Every approved signal is an immutable record — prove what you knew, and when.
            </p>
          </div>
          <Button data-testid={SIGNALS.exportCsv} variant="outline" onClick={exportCsv} disabled={exporting || !rows.length}
            className="rounded-full">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>

        <SignalsTabs />

        <div className="mt-4 rounded-xl border border-border/60 bg-card/30">
          {loading ? (
            <div className="px-4 py-16 text-center text-sm text-muted-foreground">Loading…</div>
          ) : rows.length ? (
            rows.map((r) => {
              const sev = SEVERITY[r.severity] || SEVERITY.low;
              return (
                <Link key={r.id} to={`/signals/registry/${r.id}`} data-testid={SIGNALS.registryRow}
                  className="grid grid-cols-12 items-center gap-3 border-b border-border/40 px-4 py-3.5 transition-colors last:border-0 hover:bg-muted/30">
                  <div className="col-span-12 flex items-center gap-3 sm:col-span-5">
                    <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-400" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{r.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{r.summary || "—"}</div>
                    </div>
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className={`h-2 w-2 rounded-full ${sev.dot}`} />{sev.label}
                    </span>
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Badge variant="outline" className="text-[10px] text-accent border-accent/30">{SOURCE_LABEL[r.source] || r.source}</Badge>
                  </div>
                  <div className="col-span-12 sm:col-span-3">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Approved</div>
                    <div className="text-xs text-muted-foreground">{abs(r.approved_at)} · {r.approved_by_name}</div>
                  </div>
                </Link>
              );
            })
          ) : (
            <div data-testid={SIGNALS.registryEmpty} className="px-4 py-20 text-center">
              <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
              <h2 className="mt-4 text-lg font-semibold">No approved signals yet</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Approve a signal in the <Link to="/signals/queue" className="text-accent hover:underline">review queue</Link> to create the first immutable record.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
